import { useEffect, useState } from 'react';

// ── Split view in the installed app (Calem, report 60878cab, 3rd opening) ────────────────────────────────────────
// "When the user downloads the system as an app on their desktop, allow the split screen to happen the same way."
// An installed window has no browser tabs, so Chrome's own split view cannot be used there. This is SLF's own: the
// window turns into a frame (components/SplitView, at SPLIT_PATH) holding two pages of this same site side by side,
// each in an iframe of its own. Three things are kept here, away from the screens:
// · the frame's address — the two pages are in it, so a refresh keeps the pair;
// · the registry of open pages — every other SLF window says where it is and what it is called over a
//   BroadcastChannel, and the frame's empty side lists them as bubbles to pick from;
// · the way a page inside one side talks to the frame round it — window.postMessage, same origin only.

export const SPLIT_PATH = '/split';

/** Only an address of this site is ever put into a side: a path of this origin, never another host or a scheme, and
 *  never the split frame itself (a frame inside a frame). '' = not one. */
export function samePagePath(u: string | null | undefined): string {
  const s = String(u || '').trim();
  if (!s.startsWith('/') || s.startsWith('//') || s.includes('\\')) return '';
  try {
    const at = new URL(s, window.location.origin);
    if (at.origin !== window.location.origin || at.pathname === SPLIT_PATH) return '';
    return `${at.pathname}${at.search}${at.hash}`;
  } catch { return ''; }
}

/** The frame's own address: the page on the left and the page on the right ('' = a side still to be chosen). */
export function splitHref(left: string, right: string): string {
  const q = new URLSearchParams();
  if (left) q.set('l', left);
  if (right) q.set('r', right);
  return `${SPLIT_PATH}?${q.toString()}`;
}

/** Is this page one side of a split view? Then a right-click's "Open in split view" goes to the frame round it. */
export function inSplitSide(): boolean {
  try {
    return window.parent !== window && window.parent.location.pathname === SPLIT_PATH;
  } catch { return false; }   // a frame of another site: not ours
}

// What a side tells the frame: where it is now (its page moves on its own as it is used), and what to open.
export type SideMessage = { slfSplit: 'here'; route: string; title: string } | { slfSplit: 'open'; url: string };

/** "Open in split view": from a single page, this window becomes the frame with this page on the left and the chosen
 *  one on the right; from a side of a frame already open, the frame is asked to place it (components/SplitView). */
export function openInSplit(url: string): void {
  const path = samePagePath(url);
  if (!path) { window.open(url, '_blank'); return; }   // another site cannot sit in a side
  if (inSplitSide()) {
    const m: SideMessage = { slfSplit: 'open', url: path };
    window.parent.postMessage(m, window.location.origin);
    return;
  }
  window.location.assign(splitHref(`${window.location.pathname}${window.location.search}${window.location.hash}`, path));
}

// ── The registry of open pages ─────────────────────────────────────────────────────────────────────────────────
// Each SLF window answers "who is open?" with its address and its name, says so again when either changes, and says
// goodbye when it closes. A window that dies without saying goodbye drops off the list after a few unanswered asks.
const CHANNEL = 'slf-open-pages';
type RegistryMessage =
  | { kind: 'who' }
  | { kind: 'here'; id: string; route: string; title: string }
  | { kind: 'gone'; id: string };

const myId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
let channel: BroadcastChannel | null = null;
let lastSaid = '';

/** The page's own name, as its window title gives it: "<page> · <brand> System" without the brand part. */
const pageTitle = (): string => {
  const parts = document.title.split(' · ');
  return (parts.length > 1 ? parts.slice(0, -1) : parts).join(' · ').trim();
};

/** Say where this window is. A side of a split view tells its frame instead and stays off the list: it is already
 *  on screen in that window. Repeats are dropped, unless the frame or another window asked. */
export function announcePage(asked = false): void {
  if (window.top !== window && !inSplitSide()) return;   // a page shown inside some other page is no window of its own
  const route = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const title = pageTitle();
  const said = `${route}\n${title}`;
  if (!asked && said === lastSaid) return;
  lastSaid = said;
  if (inSplitSide()) {
    const m: SideMessage = { slfSplit: 'here', route, title };
    window.parent.postMessage(m, window.location.origin);
    return;
  }
  if (typeof BroadcastChannel === 'undefined') return;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = (e: MessageEvent) => { if ((e.data as RegistryMessage)?.kind === 'who') announcePage(true); };
    window.addEventListener('pagehide', () => {
      const bye: RegistryMessage = { kind: 'gone', id: myId };
      channel?.postMessage(bye);
      lastSaid = '';
    });
    window.addEventListener('pageshow', () => announcePage(true));
  }
  const m: RegistryMessage = { kind: 'here', id: myId, route, title };
  channel.postMessage(m);
}

export type OpenPage = { id: string; route: string; title: string; seen: number };

/** The SLF pages open in the user's other windows, in the order they first answered, one bubble per address. */
export function useOpenPages(): OpenPage[] {
  const [pages, setPages] = useState<OpenPage[]>([]);
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(CHANNEL);
    const known = new Map<string, OpenPage>();
    const show = () => {
      const routes: string[] = [];
      const list: OpenPage[] = [];
      known.forEach(p => { if (routes.indexOf(p.route) < 0) { routes.push(p.route); list.push(p); } });
      setPages(list);
    };
    ch.onmessage = (e: MessageEvent) => {
      const d = e.data as RegistryMessage;
      if (d?.kind === 'here' && typeof d.id === 'string') {
        const route = samePagePath(d.route);
        if (!route) return;
        known.set(d.id, { id: d.id, route, title: String(d.title || ''), seen: Date.now() });
        show();
      } else if (d?.kind === 'gone' && typeof d.id === 'string') {
        if (known.delete(d.id)) show();
      }
    };
    const ask = () => {
      const stale = Date.now() - 12000;   // missed two asks in a row
      const gone: string[] = [];
      known.forEach(p => { if (p.seen < stale) gone.push(p.id); });
      gone.forEach(id => known.delete(id));
      if (gone.length) show();
      const who: RegistryMessage = { kind: 'who' };
      ch.postMessage(who);
    };
    ask();
    const timer = window.setInterval(ask, 5000);
    return () => { window.clearInterval(timer); ch.close(); };
  }, []);
  return pages;
}
